import React, { useEffect, useState } from "react";
import axios from "axios";
import { History, RefreshCw } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { PaginationBar } from "./PaginationBar";

const PER_PAGE = 15;
const STATUS = {
  success: ["Başarılı", "bg-green-100 text-green-800"],
  partial: ["Kısmi", "bg-amber-100 text-amber-800"],
  failed: ["Başarısız", "bg-red-100 text-red-800"],
};
const formatSize = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(2)} MB` : `${((b || 0) / 1024).toFixed(1)} KB`);

const HistoryRow = ({ item }) => {
  const [label, cls] = STATUS[item.status] || STATUS.failed;
  return (
    <div className="p-3 border rounded-lg bg-white" data-testid={`backup-history-row-${item.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="font-medium">{new Date(item.timestamp).toLocaleString("tr-TR")}</span>
          <Badge variant="outline">{item.trigger === "manual" ? "Manuel" : "Otomatik"}</Badge>
          {item.triggered_by && <span className="text-xs text-gray-500">{item.triggered_by}</span>}
        </div>
        <div className="flex items-center gap-3 text-xs text-gray-600">
          <span>{formatSize(item.total_size)}</span>
          <span>{item.duration_seconds} sn</span>
          <span className={`px-2 py-0.5 rounded-full font-medium ${cls}`}>{label}</span>
        </div>
      </div>
      {item.files?.length > 0 && <div className="text-xs text-gray-500 mt-1 break-all">{item.files.map((f) => f.name).join(" • ")}</div>}
      {item.errors?.length > 0 && <div className="text-xs text-red-600 mt-1 break-words">{item.errors.join("; ")}</div>}
    </div>
  );
};

export const BackupHistoryCard = ({ api, sessionToken }) => {
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ history: [], total: 0 });
  const [loading, setLoading] = useState(false);

  const load = async (p = page) => {
    setLoading(true);
    try {
      const res = await axios.get(`${api}/backup/history`, { params: { session_token: sessionToken, limit: PER_PAGE, skip: (p - 1) * PER_PAGE } });
      setData(res.data);
      setPage(p);
    } catch (e) {
      console.error("Yedekleme geçmişi alınamadı:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(1); }, []);

  return (
    <Card data-testid="backup-history-card">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center gap-2"><History className="h-5 w-5" /> Yedekleme Geçmişi</span>
          <Button variant="outline" size="sm" onClick={() => load(page)} disabled={loading} data-testid="backup-history-refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {data.history.length === 0 ? (
          <p className="text-center py-6 text-sm text-gray-500" data-testid="backup-history-empty">
            Henüz kayıtlı yedekleme geçmişi yok. Bir sonraki manuel veya otomatik yedeklemeden itibaren burada listelenecek.
          </p>
        ) : (
          data.history.map((item) => <HistoryRow key={item.id} item={item} />)
        )}
        <PaginationBar page={page} total={data.total} perPage={PER_PAGE} onPageChange={load} loading={loading} label="kayıt" testIdPrefix="backup-history-pagination" />
      </CardContent>
    </Card>
  );
};
